/**
 * playground.js — the editor, and the host side of the session-source override.
 *
 * The engine's envoy already knows how to take a file's source from
 * sessionStorage instead of fetching it (see loader.ts, SESSION_SOURCE_PREFIX).
 * Nothing here talks to the engine: the preview is framed on an origin of its
 * own, which stores nothing, so whatever a workspace's pages run can reach none
 * of the files kept here. Each run hands the sources to preview.html there,
 * which writes them where the envoy looks; everything else the preview asks
 * for comes back here through its service worker (sw.js, relay.js).
 */

import { EditorView, keymap, lineNumbers, highlightActiveLine,
         highlightActiveLineGutter, drawSelection, rectangularSelection,
         crosshairCursor, highlightSpecialChars } from '@codemirror/view';
import { EditorState, Compartment, Prec } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { bracketMatching, indentOnInput, foldGutter, foldKeymap,
         syntaxHighlighting, HighlightStyle } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { rvmarkLanguage } from './rvmark-lang.js';
import { rvmarkFolding, rvmarkWrapIndent, continueOutlineCommand } from './outline.js';
import { emphasisKeymap, starWrapsSelection } from './emphasis.js';
import { richPaste } from './paste.js';
import { indentationMarkers } from '@replit/codemirror-indentation-markers';
import * as fs from './fs.js';
import { directory, extract, download, downloadZip } from './zip.js';
import { saveSite as saveSiteZip } from './site.js';
import { hidePreview, togglePreview } from './panes.js';
import { ask, tell } from './dialogs.js';

// ── Theme ─────────────────────────────────────────────────────────────────────
// The page follows the OS via prefers-color-scheme, so the editor has to as
// well. CodeMirror themes are static extensions, so the live half goes through
// a compartment that is reconfigured when the media query changes.
//
// The colours are the page's own CSS variables rather than a theme package's:
// one palette for the whole page, and a syntax scheme that cannot drift away
// from the chrome around it. Only `dark` has to be told to CodeMirror, because
// that flag is what makes it pick dark defaults for the pieces not named here.

const themeCompartment = new Compartment();

const PALETTE = {
  light: { variable: '#0b6a53', type: '#7a3d9e', keyword: '#a03a1f', property: '#2a5db0',
           string: '#8a5a00', decorator: '#8a2f6b', punct: '#8a8a8a', code: '#3d6b2e' },
  dark:  { variable: '#6fd7b4', type: '#c9a2ea', keyword: '#f0916c', property: '#8ab6f0',
           string: '#e0b96a', decorator: '#e79cc9', punct: '#7a7f87', code: '#a8d68f' },
};

function editorTheme(dark) {
  const c = dark ? PALETTE.dark : PALETTE.light;
  const style = HighlightStyle.define([
    { tag: t.list,                        color: c.punct, fontWeight: 'bold' },
    { tag: t.punctuation,                 color: c.punct },
    { tag: t.keyword,                     color: c.keyword, fontWeight: 'bold' },
    { tag: t.operator,                    color: c.keyword },
    { tag: t.typeName,                    color: c.type },
    { tag: t.variableName,                color: c.variable },
    { tag: t.propertyName,                color: c.property },
    { tag: t.string,                      color: c.string },
    { tag: t.annotation,                  color: c.decorator },
    { tag: t.namespace,                   color: c.type },
    { tag: t.definition(t.variableName),  color: c.variable, fontWeight: 'bold' },
    { tag: t.definition(t.annotation),    color: c.decorator, fontWeight: 'bold' },
    { tag: t.definition(t.namespace),     color: c.type, fontWeight: 'bold' },
    { tag: t.escape,                      color: c.keyword },
    { tag: t.url,                         color: c.property, textDecoration: 'underline' },
    { tag: t.monospace,                   color: c.code },
    { tag: t.strong,                      fontWeight: 'bold' },
    { tag: t.emphasis,                    fontStyle: 'italic' },
  ]);
  // Transparent surfaces: the pane's own background shows through, so the
  // editor is the same colour as the chrome it sits in whichever way it goes.
  return [
    EditorView.theme({
      '&':            { color: 'var(--fg)', backgroundColor: 'transparent' },
      '.cm-content':  { caretColor: 'var(--fg)' },
      // --hang is set per line by rvmarkWrapIndent.
      '.cm-line':     { paddingLeft: 'calc(var(--line-pad) + var(--hang, 0ch))',
                        textIndent: 'calc(-1 * var(--hang, 0ch))' },
      // text-indent inherits into inline-block widgets (tabs, fold markers).
      '.cm-line > *': { textIndent: '0' },
      '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--fg)' },
      '.cm-gutters':  { backgroundColor: 'var(--gutter)', color: 'var(--muted)', border: 'none' },
      // Translucent: the selection layer is drawn beneath the lines.
      '.cm-activeLine':       { backgroundColor: 'color-mix(in srgb, CanvasText 5%, transparent)' },
      '.cm-activeLineGutter': { backgroundColor: 'var(--surface)' },
      '.cm-selectionBackground': { backgroundColor: 'var(--highlight)' },
      '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground':
                              { backgroundColor: 'Highlight' },
      '.cm-selectionMatch':   { backgroundColor: 'var(--highlight)' },
      '.cm-panels':   { backgroundColor: 'var(--pane)', color: 'var(--fg)' },
      // The markers assume CodeMirror's default 2px line padding.
      '.cm-indent-markers::before': { left: 'var(--line-pad)' },
    }, { dark }),
    syntaxHighlighting(style),
  ];
}

const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');

// ── Seeded workspaces ─────────────────────────────────────────────────────────
// ?workspace=<zip URL> opens a workspace of its own, made from that zip and kept
// apart from the main one. It is kept for a day after it was last opened, so a
// reader leafing through sidepanels finds their edits again, and is made afresh
// from the zip after that.

/** The zip this workspace is made from; null for the main workspace. */
const SEED = (() => {
  try {
    const url = new URL(new URLSearchParams(location.search).get('workspace'));
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch (_) {
    return null;
  }
})();

/**
 * The editor's origin, with the preview's: localhost and 127.0.0.1 locally.
 * Seeded workspaces preview on an origin of their own, so none can reach the
 * main workspace's preview.
 */
const ORIGINS = location.hostname === 'localhost' || location.hostname === '127.0.0.1'
  ? { editor: `${location.protocol}//localhost:${location.port}`,
      preview: `${location.protocol}//127.0.0.1:${location.port}` }
  : { editor: location.origin.replace(/\/\/(seeded-)?preview\./, '//editor.'),
      preview: location.origin.replace('//editor.', SEED ? '//seeded-preview.' : '//preview.') };

const PREVIEW = ORIGINS.preview;

/** Where every run starts on the preview origin. */
const PREVIEW_BOOT = PREVIEW + '/preview.html';

/** This tab's prefix on the preview origin; see sw.js. */
const TAB = Array.from(crypto.getRandomValues(new Uint8Array(16)),
                       (b) => b.toString(16).padStart(2, '0')).join('');

/** The file the built page was built from — the stamp's default, overridden per run.
 *  Must match FILE in build.mjs. */
const PAGE_FILE = 'scratch.rv.md';

/** Which file the editor is showing, across reloads. */
const CURRENT_KEY = 'rvmark-playground:current';

/** Which directories are collapsed, across reloads. */
const COLLAPSED_KEY = 'rvmark-playground:collapsed-dirs';

/** The name a fresh buffer gets. */
const DEFAULT_FILE = 'index.rv.md';

const DAY = 24 * 60 * 60 * 1000;

const UNFETCHED = 'Unable to fetch example.';
const FALLBACK  = 'Unable to fetch example, falling back to local storage.';
const BUSY      = 'Same workspace open in another tab.';

/**
 * What the editor remembers about the workspace across reloads. The main
 * workspace keeps it in localStorage; a seeded one beside its files, so it goes
 * when they do. Read once the workspace is loaded; see seed().
 */
const prefs = SEED
  ? {
      data: {},
      get(key) { return this.data[key] ?? null; },
      set(key, value) { this.data[key] = value; fs.keepBeside('meta', JSON.stringify(this.data)); },
      remove(key) { delete this.data[key]; fs.keepBeside('meta', JSON.stringify(this.data)); },
    }
  : {
      get: (key) => localStorage.getItem(key),
      set: (key, value) => localStorage.setItem(key, value),
      remove: (key) => localStorage.removeItem(key),
    };

const frame     = document.getElementById('frame');
const status    = document.getElementById('status');
const fileLabel = document.getElementById('file-label');
const treeList  = document.getElementById('tree-list');
const treeMenu  = document.getElementById('tree-menu');
const fileInput = document.getElementById('file-input');
const zipInput  = document.getElementById('zip-input');
const dirInput  = document.getElementById('dir-input');

document.getElementById('shortcuts-btn').addEventListener('click', () => shortcutsOpen());
document.getElementById('run-btn').addEventListener('click', () => run());

// ── Which file is open ────────────────────────────────────────────────────────
// The editor shows one file at a time; the filesystem holds the rest. Switching
// flushes the buffer to the filesystem first, so an unrun edit is never lost to
// a click in the tree.

let current = null;

/** Each file's editor state while another is shown: undo history, selection, scroll. */
const parked = new Map();

function park() {
  if (current !== null) parked.set(current, { state: view.state, scroll: view.scrollSnapshot() });
}

/** With no file open the editor takes no typing: there is nowhere for it to go. */
const lockCompartment = new Compartment();
const lock = (on) => [EditorState.readOnly.of(on), EditorView.editable.of(!on)];

/** Put `text` in the editor, as the parked state if that still holds the same text. */
function show(text, kept) {
  const same = kept?.state.doc.toString() === text;
  view.setState(same ? kept.state : makeState(text));
  // A state made or parked under the other colour scheme still carries it.
  view.dispatch({ effects: [
    themeCompartment.reconfigure(editorTheme(darkQuery.matches)),
    lockCompartment.reconfigure(lock(current === null)),
    ...(same ? [kept.scroll] : []),
  ] });
}

function flush() {
  if (current !== null) fs.write(current, view.state.doc.toString());
}

function openFile(path, { focus = true } = {}) {
  if (fs.isBinary(path)) return;
  if (path === current) { if (focus) view.focus(); return; }
  flush();
  park();
  setCurrent(path);
  show(fs.read(path) ?? '', parked.get(path));
  parked.delete(path);
  renderTree();
  if (focus) view.focus();
}

function setCurrent(path) {
  current = path;
  prefs.set(CURRENT_KEY, path);
  fitLabel();
}

/** The current path, its leading folders traded for '…/' until it fits the bar.
 *  The file name itself is only ever cut by CSS. */
function fitLabel() {
  fileLabel.title = current ?? '';
  if (current === null) { fileLabel.textContent = ''; return; }
  const parts = current.split('/');
  fileLabel.textContent = current;
  for (let i = 1; i < parts.length && fileLabel.scrollWidth > fileLabel.clientWidth; i++) {
    fileLabel.textContent = '…/' + parts.slice(i).join('/');
  }
}

// The bar for the window and the gutters; the status because its text changes
// the room left over.
new ResizeObserver(fitLabel).observe(document.getElementById('bar'));
new ResizeObserver(fitLabel).observe(status);

/** Open `prefer` if it is text, else the first rvmark file, else any text file. */
function openFirst(prefer, { focus = false } = {}) {
  const texts = fs.paths().filter((p) => !fs.isBinary(p));
  const path = texts.includes(prefer) ? prefer : texts.find(fs.isRvFile) ?? texts[0];
  if (path) openFile(path, { focus });
  else { closeFile(); renderTree(); }
  return path;
}

/** No file open: what is left when the last one is deleted. */
function closeFile() {
  current = null;
  prefs.remove(CURRENT_KEY);
  fitLabel();
  show('');
  // Nor is anything left rendered, or said to have been.
  handoff = null;
  frame.src = 'about:blank';
  status.textContent = '';
  status.classList.remove('dirty');
}

// ── The tree ──────────────────────────────────────────────────────────────────
// Paths are flat strings; the tree is built from their slashes. Keys follow the
// engine's navtree (see its keymap.ts), with the list as a single tab stop.

/** Directories the reader has collapsed, by path. */
const collapsed = new Set(savedCollapsed());

function savedCollapsed() {
  try {
    const v = JSON.parse(prefs.get(COLLAPSED_KEY) || '[]');
    return Array.isArray(v) ? v : [];
  } catch (_) { return []; }
}

/** The row that holds the tab stop, by data-key. */
let activeKey = null;

function buildTree(paths) {
  const root = { dirs: new Map(), files: [] };
  for (const path of paths) {
    const parts = path.split('/');
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const dir = parts.slice(0, i + 1).join('/');
      if (!node.dirs.has(parts[i])) node.dirs.set(parts[i], { path: dir, dirs: new Map(), files: [] });
      node = node.dirs.get(parts[i]);
    }
    if (parts.at(-1)) node.files.push({ name: parts.at(-1), path });
  }
  return root;
}

function treeRow(key, label, depth) {
  // Not a button: Firefox will not start a drag from one.
  const row = document.createElement('div');
  row.className = 'tree-row';
  row.dataset.key = key;
  row.tabIndex = -1;
  row.draggable = true;
  row.textContent = label;
  row.style.paddingLeft = `${0.5 + depth * 0.9}rem`;
  row.addEventListener('focus', () => { setActive(key); });
  return row;
}

/** The row's menu, for a pointer that cannot right-click. */
function moreButton() {
  const more = document.createElement('button');
  more.type = 'button';
  more.className = 'row-menu';
  more.tabIndex = -1;
  more.textContent = '⋯';
  more.title = 'Actions';
  return more;
}

function renderLevel(node, depth, ul) {
  for (const [name, dir] of [...node.dirs].sort(([a], [b]) => a.localeCompare(b))) {
    const open = !collapsed.has(dir.path);
    const li = document.createElement('li');
    li.setAttribute('role', 'treeitem');
    li.setAttribute('aria-expanded', String(open));
    const row = treeRow(dir.path + '/', name + '/', depth);
    row.classList.add('dir-row');
    row.addEventListener('click', () => toggleDir(dir.path));
    li.append(row, moreButton());
    if (open) {
      const group = document.createElement('ul');
      group.setAttribute('role', 'group');
      renderLevel(dir, depth + 1, group);
      li.append(group);
    }
    ul.append(li);
  }
  for (const { name, path } of node.files) {
    const li = document.createElement('li');
    li.setAttribute('role', 'treeitem');
    const row = treeRow(path, name, depth);
    row.classList.add('file-row');
    if (path === current) { row.classList.add('current'); li.setAttribute('aria-selected', 'true'); }
    row.title = path;
    row.addEventListener('click', () => openFile(path));
    li.append(row, moreButton());
    ul.append(li);
  }
}

function renderTree() {
  const paths = [...fs.paths(), ...fs.dirs()];
  const hadFocus = treeList.contains(document.activeElement);
  treeList.replaceChildren();

  // A directory that is gone is forgotten, so one made later by its name starts open.
  for (const dir of [...collapsed]) {
    if (!paths.some((p) => p.startsWith(dir + '/'))) collapsed.delete(dir);
  }
  prefs.set(COLLAPSED_KEY, JSON.stringify([...collapsed]));

  if (paths.length === 0) {
    const empty = document.createElement('p');
    empty.id = 'tree-empty';
    empty.textContent = 'No files.';
    treeList.append(empty);
    return;
  }

  const ul = document.createElement('ul');
  ul.setAttribute('role', 'tree');
  renderLevel(buildTree(paths), 0, ul);
  treeList.append(ul);

  const rows = treeRows();
  const active = rows.find(r => r.dataset.key === activeKey)
              ?? rows.find(r => r.dataset.key === current)
              ?? rows[0];
  setActive(active.dataset.key);
  if (hadFocus) active.focus();
}

function treeRows() {
  return [...treeList.querySelectorAll('.tree-row')];
}

function rowFor(key) {
  return treeRows().find(r => r.dataset.key === key);
}

function setActive(key) {
  activeKey = key;
  for (const r of treeRows()) r.tabIndex = r.dataset.key === key ? 0 : -1;
}

function toggleDir(path) {
  if (!collapsed.delete(path)) collapsed.add(path);
  activeKey = path + '/';
  renderTree();
  treeRows().find(r => r.dataset.key === activeKey)?.focus();
}

treeList.addEventListener('keydown', (e) => {
  const rows = treeRows();
  const row = document.activeElement;
  const i = rows.indexOf(row);
  if (i === -1) return;
  const li = row.parentElement;
  const isDir = row.classList.contains('dir-row');
  const open = li.getAttribute('aria-expanded') === 'true';
  const rowOf = (item) => item?.querySelector(':scope > .tree-row');

  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); row.click(); return; }
  if (e.key === 'F2') { e.preventDefault(); startRename(row.dataset.key); return; }
  if (e.key === 'Delete') { e.preventDefault(); deleteRow(row.dataset.key); return; }

  let target;
  if (e.altKey && e.key === 'ArrowDown') target = rowOf(li.nextElementSibling);
  else if (e.altKey && e.key === 'ArrowUp') target = rowOf(li.previousElementSibling);
  else if (e.key === 'ArrowDown') target = rows[i + 1];
  else if (e.key === 'ArrowUp') target = rows[i - 1];
  else if (e.key === 'Home') target = rows[0];
  else if (e.key === 'End') target = rows.at(-1);
  else if (e.key === 'ArrowRight' && isDir) {
    if (!open) { e.preventDefault(); toggleDir(row.dataset.key.slice(0, -1)); return; }
    target = rows[i + 1];
  } else if (e.key === 'ArrowLeft') {
    if (isDir && open) { e.preventDefault(); toggleDir(row.dataset.key.slice(0, -1)); return; }
    target = rowOf(li.parentElement.closest('li'));
  } else return;

  e.preventDefault();
  target?.focus();
});

// ── Row actions ───────────────────────────────────────────────────────────────
// A row is named by its data-key: a file's path, or a directory's with a
// trailing slash, which is also how fs.js tells the two apart.

/** The `dir/` key a new entry made from a row goes in: the folder itself, or the one a file is in. */
const dirOf = (key) => key.slice(0, key.lastIndexOf('/') + 1);

const ROW_ACTIONS = [
  { label: 'New file',   run: (key) => startNew(dirOf(key)) },
  { label: 'New folder', run: (key) => startNew(dirOf(key), { folder: true }) },
  { label: 'Rename',   run: (key) => startRename(key) },
  { label: 'Move to…', run: (key) => moveOpen(key), when: (key) => destinations(key).length > 0 },
  { label: 'Save',     run: (key) => saveFile(key), when: (key) => !key.endsWith('/') },
  { label: 'Delete',   run: (key) => deleteRow(key) },
];

/** What the tree as a whole offers, from the button on its bar. */
const TREE_ACTIONS = [
  { label: 'New file',     run: () => startNew() },
  { label: 'New folder',   run: () => startNew('', { folder: true }) },
  { label: 'Open files…',  run: () => fileInput.click() },
  // iOS has no folder picker.
  { label: 'Open folder…', run: () => dirInput.click(), when: () => 'webkitdirectory' in dirInput },
  { label: 'Open zip…',    run: () => zipInput.click() },
  { label: 'Save all (.zip)',   run: () => saveZip() },
  { label: 'Export built site', run: () => saveSite() },
  { label: 'Empty workspace…', run: () => emptyOpen(), when: () => !SEED },
  { label: 'Reset…',           run: () => reset(),     when: () => !!SEED },
];

/** What focus goes back to when the menu closes; null for the active row. */
let menuOwner = null;

function menuOpen(row, x, y) {
  setActive(row.dataset.key);
  menuShow(ROW_ACTIONS, row.dataset.key, x, y);
}

function menuShow(actions, key, x, y, owner = null) {
  menuOwner = owner;
  treeMenu.replaceChildren(...actions
    .filter((action) => !action.when || action.when(key))
    .map(({ label, run }) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.setAttribute('role', 'menuitem');
      item.textContent = label;
      item.addEventListener('click', () => { menuClose(); run(key); });
      // One highlight for pointer and keys alike: hovering moves the selection.
      item.addEventListener('pointermove', () => item.focus());
      return item;
    }));
  treeMenu.hidden = false;
  const { width, height } = treeMenu.getBoundingClientRect();
  treeMenu.style.left = `${Math.max(0, Math.min(x, innerWidth - width))}px`;
  treeMenu.style.top  = `${Math.max(0, Math.min(y, innerHeight - height))}px`;
  treeMenu.firstChild.focus();
}

function menuClose({ refocus = true } = {}) {
  if (treeMenu.hidden) return;
  treeMenu.hidden = true;
  if (refocus) (menuOwner ?? rowFor(activeKey))?.focus();
}

const filesBtn = document.getElementById('files-btn');
let filesMenuWasOpen = false;

// The press that lands on the button also closes the menu, as any press outside
// it does; what the click then does depends on what was open before the press.
filesBtn.addEventListener('pointerdown', () => {
  filesMenuWasOpen = !treeMenu.hidden && menuOwner === filesBtn;
});
filesBtn.addEventListener('click', () => {
  if (filesMenuWasOpen) { filesMenuWasOpen = false; return; }
  const rect = filesBtn.getBoundingClientRect();
  menuShow(TREE_ACTIONS, null, rect.left, rect.bottom, filesBtn);
});

treeList.addEventListener('contextmenu', (e) => {
  const row = e.target.closest('li')?.querySelector(':scope > .tree-row');
  e.preventDefault();
  // Below the last row there is only the tree itself to make something in.
  if (!row) { menuShow(TREE_ACTIONS.slice(0, 2), null, e.clientX, e.clientY); return; }
  const rect = row.getBoundingClientRect();
  menuOpen(row, e.clientX || rect.left, e.clientY || rect.bottom);
});

treeList.addEventListener('click', (e) => {
  if (!e.target.classList.contains('row-menu')) return;
  const rect = e.target.getBoundingClientRect();
  menuOpen(e.target.previousElementSibling, rect.left, rect.bottom);
});

/** Arrow keys walk the buttons of a menu or a dialog. True if the key was one. */
function arrowsWalk(e, box) {
  const step = { ArrowDown: 1, ArrowUp: -1 }[e.key];
  if (!step) return false;
  e.preventDefault();
  const items = [...box.querySelectorAll('button')];
  items[(items.indexOf(document.activeElement) + step + items.length) % items.length].focus();
  return true;
}

treeMenu.addEventListener('keydown', (e) => {
  if (arrowsWalk(e, treeMenu)) return;
  if (e.key === 'Escape' || e.key === 'Tab') {
    e.preventDefault();
    menuClose();
  }
});

document.addEventListener('pointerdown', (e) => {
  if (!treeMenu.contains(e.target)) menuClose({ refocus: false });
});
// A click in the preview never reaches this document.
window.addEventListener('blur', () => menuClose({ refocus: false }));

/**
 * Swap a row for a text field. `commit` gets the trimmed text on Enter or on
 * losing focus, unless it is empty; Escape drops it.
 */
function editRow(row, value, selectTo, commit) {
  const input = document.createElement('input');
  input.className = 'tree-edit';
  input.value = value;
  input.spellcheck = false;
  input.autocapitalize = 'off';
  input.style.paddingLeft = row.style.paddingLeft;

  let done = false;
  const finish = async (keep) => {
    if (done) return;
    done = true;
    const typed = input.value.trim();
    if (keep && typed) await commit(typed);
    renderTree();
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); finish(true); }
    else if (e.key === 'Escape') { e.preventDefault(); finish(false); }
  });
  input.addEventListener('blur', () => finish(true));

  row.parentElement.replaceChildren(input, ...row.parentElement.querySelectorAll(':scope > ul'));
  input.focus();
  input.setSelectionRange(0, selectTo);
}

function startRename(key) {
  const row = rowFor(key);
  if (!row) return;
  const isDir = key.endsWith('/');
  const path = isDir ? key.slice(0, -1) : key;
  const cut  = path.lastIndexOf('/') + 1;
  const name = path.slice(cut);
  editRow(row, name, isDir ? name.length : fs.extStart(name), async (typed) => {
    const target = fs.normalizePath(path.slice(0, cut) + typed);
    if (target) await moveRow(key, target + (isDir ? '/' : ''));
  });
}

async function moveRow(from, to) {
  if (from === to || (from.endsWith('/') && to.startsWith(from))) return;
  const blocker = fs.conflict(to);
  if (blocker) { await tell(`${blocker} already exists.`); return; }
  flush();
  const plan  = fs.moves(from, to);
  const clash = [...plan.values()].filter((t) => !t.endsWith('/') && fs.has(t) && !plan.has(t));
  if (clash.length && !(await ask(
        `${clash.join(', ')} ${clash.length === 1 ? 'exists' : 'exist'}. Overwrite?`))) {
    return;
  }

  fs.move(from, to);
  for (const [old, now] of [...plan].filter(([old]) => parked.has(old))) {
    parked.set(now, parked.get(old));
    parked.delete(old);
  }
  if (from.endsWith('/')) {
    for (const dir of [...collapsed]) {
      if (!(dir + '/').startsWith(from)) continue;
      collapsed.delete(dir);
      collapsed.add(to.slice(0, -1) + dir.slice(from.length - 1));
    }
  }
  if (plan.has(current)) {
    setCurrent(plan.get(current));
  } else if (clash.includes(current)) {
    // The open file was the one replaced; the buffer has to show what replaced it.
    const path = current;
    current = null;
    openFirst(path);
  }
  activeKey = to;
}

// ── Moving ────────────────────────────────────────────────────────────────────
// A row goes into another directory by being dragged onto it, or by naming the
// directory in a dialog, which is the way that needs no drag.

async function moveInto(key, dirKey) {
  const isDir = key.endsWith('/');
  const path  = isDir ? key.slice(0, -1) : key;
  await moveRow(key, dirKey + path.slice(path.lastIndexOf('/') + 1) + (isDir ? '/' : ''));
  if (dirKey) collapsed.delete(dirKey.slice(0, -1));
  renderTree();
  rowFor(activeKey)?.focus();
}

/** The `dir/` keys a row could move into: every directory but its own and those inside it. */
function destinations(key) {
  const all = new Set(['']);
  for (const path of [...fs.paths(), ...fs.dirs()]) {
    for (let cut = path.indexOf('/'); cut !== -1; cut = path.indexOf('/', cut + 1)) all.add(path.slice(0, cut + 1));
  }
  const own = key.slice(0, key.lastIndexOf('/', key.length - 2) + 1);
  return [...all].sort().filter((dir) => dir !== own && !(key.endsWith('/') && dir.startsWith(key)));
}

let moveDialog = null;

function moveOpen(key) {
  if (!moveDialog) {
    moveDialog = document.createElement('dialog');
    moveDialog.id = 'move-dialog';
    moveDialog.innerHTML = '<h2>Move to</h2><form method="dialog"></form>';
    moveDialog.addEventListener('click', (e) => { if (e.target === moveDialog) moveDialog.close(); });
    moveDialog.addEventListener('keydown', (e) => arrowsWalk(e, moveDialog));
    document.body.appendChild(moveDialog);
  }
  moveDialog.querySelector('form').replaceChildren(...destinations(key).map((dir) => {
    const choice = document.createElement('button');
    choice.textContent = dir || '/';
    choice.addEventListener('click', () => moveInto(key, dir));
    return choice;
  }));
  moveDialog.showModal();
}

const MOVE_TYPE = 'application/x-rvmark-key';

/** The directory a drop on `target` lands in: a folder row's own, else the one the row sits in. */
function dropDir(target) {
  const key = target.closest('li')?.querySelector(':scope > .tree-row')?.dataset.key ?? '';
  return key.slice(0, key.lastIndexOf('/') + 1);
}

let dropMark = null;

function markDrop(el) {
  if (el === dropMark) return;
  dropMark?.classList.remove('drop');
  dropMark = el;
  el?.classList.add('drop');
}

treeList.addEventListener('dragstart', (e) => {
  const row = e.target.closest('.tree-row');
  if (!row) return;
  e.dataTransfer.setData(MOVE_TYPE, row.dataset.key);
  e.dataTransfer.effectAllowed = 'move';
});

treeList.addEventListener('dragover', (e) => {
  const types = e.dataTransfer.types;
  if (!types.includes(MOVE_TYPE) && !types.includes('Files')) return;
  e.preventDefault();
  e.dataTransfer.dropEffect = types.includes(MOVE_TYPE) ? 'move' : 'copy';
  const dir = dropDir(e.target);
  markDrop(dir ? rowFor(dir)?.parentElement : treeList);
});

treeList.addEventListener('dragleave', (e) => {
  if (!treeList.contains(e.relatedTarget)) markDrop(null);
});
treeList.addEventListener('dragend', () => markDrop(null));

treeList.addEventListener('drop', (e) => {
  const dir = dropDir(e.target);
  markDrop(null);
  const key = e.dataTransfer.getData(MOVE_TYPE);
  if (key) {
    e.preventDefault();
    moveInto(key, dir);
  } else if (e.dataTransfer.types.includes('Files')) {
    e.preventDefault();
    addDropped(e.dataTransfer.items, dir);
  }
});

async function deleteRow(key) {
  const gone = fs.covered(key);
  const inside = gone.filter((p) => !p.endsWith('/')).length;
  const what = key.endsWith('/') && inside
    ? `${key} and the ${inside} file${inside === 1 ? '' : 's'} in it`
    : key;
  if (!(await ask(`Delete ${what}?`))) return;

  fs.remove(key);
  for (const path of gone) parked.delete(path);
  if (gone.includes(current)) {
    current = null;
    if (!openFirst()) { startNew(); return; }
  }
  renderTree();
}

/** A blank row to name a new file or folder in: at the root, or inside a `dir/` key. */
function startNew(dirKey = '', { folder = false } = {}) {
  if (dirKey) collapsed.delete(dirKey.slice(0, -1));
  renderTree();

  let group = dirKey
    ? rowFor(dirKey)?.parentElement.querySelector(':scope > ul')
    : treeList.querySelector(':scope > ul');
  if (!group) {
    group = document.createElement('ul');
    treeList.replaceChildren(group);
  }
  const li  = document.createElement('li');
  const row = treeRow('', '', dirKey.split('/').length - 1);
  li.append(row);
  group.prepend(li);

  editRow(row, '', 0, async (typed) => {
    let path = fs.normalizePath(dirKey + typed);
    if (!path) return;
    if (!folder && !path.slice(path.lastIndexOf('/') + 1).includes('.')) path += fs.EXT;
    const blocker = fs.conflict(folder ? path + '/' : path);
    if (blocker) { await tell(`${blocker} already exists.`); return; }
    if (folder) {
      activeKey = path + '/';
      if (fs.covered(activeKey).length === 0) fs.write(activeKey, '');
      return;
    }
    if (!fs.has(path)) fs.write(path, '');
    activeKey = path;
    openFile(path);
  });
}

// ── Unsaved work ──────────────────────────────────────────────────────────────
// The browser's store is for getting a session back, not for keeping work: work
// is kept once it is in a zip. Whether it is survives a reload, as the work does.

const UNSAVED_KEY = 'rvmark-playground:unsaved';
const TITLE = document.title;

let unsaved = false;
let changes = 0;

function setUnsaved(on) {
  unsaved = on;
  prefs.set(UNSAVED_KEY, on ? '1' : '0');
  document.body.classList.toggle('unsaved', on);
  document.title = TITLE + (on ? ' •' : '');
}

setUnsaved(prefs.get(UNSAVED_KEY) === '1');
fs.whenChanged(() => { changes++; if (!unsaved) setUnsaved(true); });

// A seeded workspace is kept for a day anyway, and is left by leafing on.
addEventListener('beforeunload', (e) => { if (unsaved && !SEED) e.preventDefault(); });

// ── Open and save ─────────────────────────────────────────────────────────────
// Saving is one key for every file, as a zip; a single file is saved from its
// row in the tree. Opening is two keys over two scopes, and the destructive one (a zip replacing
// the tree) is the one behind the extra modifier.

async function saveFile(path) {
  if (path === current) flush();
  // The directory is dropped: a lone file has nowhere to put it, which is the
  // reason the zip exists for anything more than one.
  const name = path.slice(path.lastIndexOf('/') + 1);
  download(name, fs.read(path) ?? await fs.blob(path));
  // A workspace of one file is saved whole by saving that file.
  if (fs.paths().length === 1) setUnsaved(false);
  note('saved ' + name);
}

/** The local date and time, as a download is named by it: 2026-10-01-2224. */
function stamp() {
  const d = new Date(), two = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}-${two(d.getHours())}${two(d.getMinutes())}`;
}

// ── Workspace name ────────────────────────────────────────────────────────────
// Only a filename: zips are saved under it, and an opened zip's name sets it.

const NAME_KEY = 'rvmark-playground:name';
const nameInput = document.getElementById('workspace-name');

function setName(name) {
  nameInput.value = name;
  prefs.set(NAME_KEY, name);
}

setName(prefs.get(NAME_KEY) ?? '');
nameInput.addEventListener('change', () => setName(nameInput.value.trim()));
nameInput.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') nameInput.value = prefs.get(NAME_KEY) ?? '';
  if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); nameInput.blur(); }
});

/** What a saved zip's name starts with. */
const nameBase = () => nameInput.value.trim().replace(/[\\/:*?"<>|]/g, '-') || 'rvmark';

/** The name a zip was saved under: its stamp, and the counter a browser adds to a name taken, removed. */
const nameFrom = (filename) => {
  const base = filename.replace(/\.zip$/i, '').replace(/\s*\(\d+\)$/, '').replace(/-\d{4}-\d\d-\d\d-\d{4}$/, '');
  return base === 'rvmark' ? '' : base;
};

/** True once the zip has been handed to the browser. */
async function saveZip() {
  flush();
  const paths = fs.paths();
  if (paths.length === 0) { note('nothing to save'); return false; }
  note('saving…');
  const at = changes;
  try {
    const entries = fs.dirs().map((dir) => [dir, '']);
    for (const path of paths) entries.push([path, fs.read(path) ?? await fs.blob(path)]);
    await downloadZip(`${nameBase()}-${stamp()}.zip`, entries);
    // The zip takes a while; what was changed meanwhile may not be in it.
    if (changes === at) setUnsaved(false);
    note(`saved ${paths.length} file${paths.length === 1 ? '' : 's'}`);
    return true;
  } catch (err) {
    console.warn('[playground] zip failed:', err);
    await tell('The zip could not be saved: ' + err.message);
    return false;
  }
}

// ── Emptying ──────────────────────────────────────────────────────────────────
// Everything goes at once, so it is asked twice: first whether to save, and
// then, for whoever declines, whether they meant it.

let emptyDialog = null;

function emptyOpen() {
  if (fs.isEmpty()) return;
  // With folders and no files there is nothing a zip would be for.
  if (fs.paths().length === 0) { emptyAfter('lose'); return; }
  if (!emptyDialog) {
    emptyDialog = document.createElement('dialog');
    emptyDialog.id = 'empty-dialog';
    emptyDialog.innerHTML =
      '<h2>Empty workspace</h2><form method="dialog">' +
      '<button value="save">Save a zip first</button>' +
      '<button value="lose">Do not save</button>' +
      '<button value="">Cancel</button></form>';
    emptyDialog.addEventListener('click', (e) => { if (e.target === emptyDialog) emptyDialog.close(''); });
    emptyDialog.addEventListener('keydown', (e) => arrowsWalk(e, emptyDialog));
    emptyDialog.addEventListener('close', () => {
      if (emptyDialog.returnValue) emptyAfter(emptyDialog.returnValue);
    });
    document.body.appendChild(emptyDialog);
  }
  // Escape closes without a button, and must not leave the last answer standing.
  emptyDialog.returnValue = '';
  emptyDialog.showModal();
}

async function emptyAfter(choice) {
  if (choice === 'save') {
    if (!(await saveZip())) return;
    if (!(await ask('The zip has been handed to the browser. Empty the workspace now?'))) return;
  } else if (!(await ask('Everything in the workspace will be irretrievably lost. Continue?'))) {
    return;
  }
  fs.clear();
  setUnsaved(false);
  setName('');
  parked.clear();
  collapsed.clear();
  closeFile();
  startNew();
}

async function saveSite() {
  flush();
  note('building site…');
  try {
    const saved = await saveSiteZip(`deployable-${nameBase()}-${stamp()}.zip`, (warnings) =>
      ask(`The site has problems:\n\n${warnings.map((w) => '• ' + w).join('\n')}\n\nSave it anyway?`));
    note(saved ? 'saved site' : 'site not saved');
  } catch (err) {
    note("couldn't build the site");
    tell("The site couldn't be built: " + err.message);
    console.warn('[playground] site build failed:', err);
  }
}

/** Open loose files, adding them to the tree. A name already taken is kept by
 *  suffixing rather than overwriting — an import should not silently eat work. */
async function openFiles(fileList) {
  // A file picked as part of a folder carries its path within that folder.
  await addFiles(fileList.map((file) => [file.webkitRelativePath || file.name, file]));
}

// Names handed out while their files are still being written, which fs.has
// does not see yet.
const pasting = new Set();

/** A pasted image or video, stored beside the open file. */
function savePasted(file) {
  if (current === null) return null;
  const ext = file.type.split('/')[1].replace('jpeg', 'jpg').replace(/\W.*/, '');
  const want = dirOf(current) + (fs.normalizePath(file.name).replace(/\//g, '').replace(/\s+/g, '-') || `pasted.${ext}`);
  const dot = fs.extStart(want);
  let path = want;
  for (let n = 1; fs.has(path) || pasting.has(path) || fs.conflict(path); n++) {
    path = `${want.slice(0, dot)}-${n}${want.slice(dot)}`;
  }
  pasting.add(path);
  return {
    src: './' + path.slice(path.lastIndexOf('/') + 1),
    write: () => fs.add(path, file)
      .then(() => { note(`pasted ${path}`); renderTree(); })
      .catch((err) => note(`couldn't save ${path}: ${err.message}`))
      .finally(() => pasting.delete(path)),
  };
}

/** Add `[relative path, File]` pairs under the `dir/` key given, or at the root. */
async function addFiles(items, dirKey = '') {
  const added = [];
  let blocked = 0;
  for (const [rel, file] of items) {
    const path = fs.uniquePath(dirKey + (fs.normalizePath(rel) || DEFAULT_FILE));
    if (fs.conflict(path)) { blocked++; continue; }
    await fs.add(path, file);
    added.push(path);
  }
  if (dirKey) collapsed.delete(dirKey.slice(0, -1));
  renderTree();
  const first = added.find((path) => !fs.isBinary(path));
  if (first) openFile(first);
  note(`opened ${added.length} file${added.length === 1 ? '' : 's'}`);
  if (blocked) {
    tell(`${blocked} file${blocked === 1 ? '' : 's'} not added: a file and a folder cannot share a name.`);
  }
}

/** Take in what was dropped from outside the browser: files, and folders with all in them. */
async function addDropped(items, dirKey) {
  // The entries have to be taken before the first await; the list is emptied after.
  const files   = [...items].filter((item) => item.kind === 'file');
  const dropped = files.map((item) => item.webkitGetAsEntry?.());
  // An item the browser gives no entry for is still a file, just not a folder.
  const found   = files.filter((_, i) => !dropped[i]).map((item) => item.getAsFile()).map((file) => [file.name, file]);
  const hollow  = [];
  const walk = async (entry, prefix) => {
    if (entry.isFile) {
      found.push([prefix + entry.name, await new Promise((ok, no) => entry.file(ok, no))]);
      return;
    }
    const reader = entry.createReader();
    let any = false;
    // A reader hands over a folder's entries in batches, and an empty one ends it.
    for (;;) {
      const batch = await new Promise((ok, no) => reader.readEntries(ok, no));
      if (!batch.length) break;
      any = true;
      for (const child of batch) await walk(child, prefix + entry.name + '/');
    }
    if (!any) hollow.push(prefix + entry.name + '/');
  };
  for (const entry of dropped.filter(Boolean)) await walk(entry, '');

  for (const dir of hollow) {
    const key = dirKey + fs.normalizePath(dir) + '/';
    if (!fs.conflict(key) && !fs.covered(key).length) fs.write(key, '');
  }
  await addFiles(found, dirKey);
}

/** Open a zip, replacing the tree. */
async function openZip(file) {
  // The directory is read before anything is asked or replaced: a file that is
  // not a zip, or one cut short, is turned away with the tree untouched.
  let entries;
  try {
    entries = await directory(file);
  } catch (err) {
    console.warn('[playground] unzip failed:', err);
    tell(err.message === 'zip64' ? 'Zips over 4 GB are not supported.' : 'That file could not be read as a zip.');
    return;
  }
  if (entries.every((entry) => entry.isDir)) { tell('That zip has no files in it.'); return; }

  const held = fs.paths().length;
  if (held && !(await ask(
        `The ${held} file${held === 1 ? '' : 's'} here will be replaced. Continue?`))) {
    return;
  }

  await fill(file, entries);
  // Prefers an rvmark file to land on: a zip may carry assets beside the text,
  // and opening a stylesheet as the first buffer would be a poor greeting.
  openFirst(undefined, { focus: true });
  const paths = fs.paths();
  note(`opened ${paths.length} file${paths.length === 1 ? '' : 's'}`);
}

/** Replace the tree with a zip's `entries`. */
async function fill(file, entries) {
  fs.clear();
  parked.clear();
  collapsed.clear();
  closeFile();
  renderTree();
  setName(nameFrom(file.name));
  note('opening…');
  try {
    for (const entry of entries) {
      if (entry.isDir) { fs.write(entry.path + '/', ''); continue; }
      const out = fs.sink(entry.path);
      await extract(file, entry, out, fs.settled);
      await out.close();
    }
    setUnsaved(false);
  } catch (err) {
    console.warn('[playground] unzip failed:', err);
    tell(`The zip could not be read to the end: ${err.message}`);
  }
}

/** The seed's zip, read as far as its directory; null if it cannot be had. */
async function fetchSeed() {
  try {
    const res = await fetch(SEED, { cache: 'no-cache' });
    if (!res.ok) return null;
    const name = decodeURIComponent(new URL(SEED).pathname.split('/').pop() || 'example.zip');
    const file = new File([await res.blob()], name);
    const entries = await directory(file);
    return entries.some((entry) => !entry.isDir) ? { file, entries } : null;
  } catch (err) {
    console.warn('[playground] seed failed:', err);
    return null;
  }
}

/** Make the workspace afresh from its zip, or keep it if the zip cannot be had. */
async function reseed() {
  const zip = await fetchSeed();
  if (!zip) {
    tell(fs.isEmpty() ? UNFETCHED : FALLBACK);
    return false;
  }
  prefs.data = { url: SEED, used: Date.now() };
  await fill(zip.file, zip.entries);
  setUnsaved(false);
  return true;
}

/** Load a seeded workspace: as it was left if that was within a day, else from its zip. */
async function seed() {
  let meta = null;
  try { meta = JSON.parse(await fs.beside('meta')); } catch (_) {}
  const kept = meta?.url === SEED && !fs.isEmpty();
  if (kept) {
    prefs.data = meta;
    collapsed.clear();
    for (const dir of savedCollapsed()) collapsed.add(dir);
    setUnsaved(prefs.get(UNSAVED_KEY) === '1');
    setName(prefs.get(NAME_KEY) ?? '');
  }
  if (!kept || Date.now() - (meta.used ?? 0) >= DAY) await reseed();
  prefs.set('url', SEED);
  prefs.set('used', Date.now());
}

async function reset() {
  if (!(await ask('Everything in the workspace will be irretrievably lost. Continue?'))) return;
  if (!(await reseed())) return;
  openFirst(undefined, { focus: true });
  run();
}

for (const input of [fileInput, dirInput]) {
  input.addEventListener('change', async () => {
    if (input.files?.length) await openFiles([...input.files]);
    input.value = ''; // so the same file can be picked twice running
  });
}

zipInput.addEventListener('change', async () => {
  if (zipInput.files?.[0]) await openZip(zipInput.files[0]);
  zipInput.value = '';
});

/** A transient line on the status bar; the next run overwrites it. */
function note(text) {
  status.textContent = text;
}

// ── Running ───────────────────────────────────────────────────────────────────
// Every run reloads the frame at preview.html, which asks for the run and is
// handed it; the built page loads after that, with the sources in place.
//
// `frame.src = …` rather than contentWindow.location.reload(): a reload re-runs
// the URL the frame is already at, which after a click inside the preview is
// whatever the visitor navigated to, not the page being edited.

/** The run preview.html is handed when it asks. */
let handoff = null;

/** Mod-Shift-Enter: focus the preview once the run's page is up. */
let focusOnReady = false;

async function run() {
  if (current === null) return;
  flush();

  // The other files go in too, not just the current one: a ref or a
  // transclusion into a sibling is resolved by the envoy against the same
  // storage, ahead of anything it would fetch.
  //
  // sessionStorage holds a few megabytes and a workspace can hold more. What
  // does not fit is left out: the envoy then fetches it, and the service worker
  // asks this page for it. So the files go in by how much they matter here.
  const sources = [];
  const hand = (name, path) => sources.push([name, fs.read(path)]);
  const texts = fs.paths().filter((path) => !fs.isBinary(path));
  hand(fs.publishedPath(current), current);
  // The page's own file must answer too — it is the address the client boots
  // from, and if the buffer has no file by that name the frame has nothing to
  // load.
  hand(PAGE_FILE, texts.find((path) => fs.publishedPath(path) === PAGE_FILE) ?? current);
  for (const path of texts.filter(fs.isRvFile)) hand(fs.publishedPath(path), path);
  for (const path of texts.filter((path) => !fs.isRvFile(path))) hand(path, path);

  // The one built page renders whichever file `target` names; see PAGE_SHIM in build.mjs.
  handoff = { type: 'rvmark-playground:run', tab: TAB, target: fs.publishedPath(current), sources };

  // What the preview asks for later is read from disk.
  await fs.settled();
  frame.src = PREVIEW_BOOT;
  status.textContent = 'ran ' + new Date().toTimeString().slice(0, 8);
  status.classList.remove('dirty');
}

/** A file the preview asked for, by its published path, as a Blob; null if there is none. */
async function answerFile(path, port) {
  if (!port) return;
  try {
    const alias = path.endsWith(fs.EXT) ? path.slice(0, -fs.EXT.length) + '.rvmark' : null;
    const name = [path, alias].find((p) => p && !p.endsWith('/') && fs.has(p));
    if (!name) { port.postMessage(null); return; }
    const text = fs.read(name);
    port.postMessage(text !== null ? new Blob([text]) : await fs.blob(name));
  } catch (_) {
    port.postMessage(null);
  }
}

/**
 * Move focus into the preview, so the tree's own keyboard handling takes over.
 * The window is ours to focus; the node inside is relay.js's, which waits for
 * the tree to mount and set its own selection first.
 */
function focusPreview() {
  frame.contentWindow?.focus();
  frame.contentWindow?.postMessage({ type: 'rvmark-playground:focus' }, PREVIEW);
}

// Ctrl/Cmd+Enter runs; Mod-Shift-Enter runs and hands over to the render, which
// is the pairing that makes the loop keyboard-complete: write, run, read.
//
// Nothing here binds Escape. Taking Tab for indentation does cost the standard
// way out of a text field, and CodeMirror's answer is tab-focus mode, already
// bound to Ctrl-m by defaultKeymap — the Tab example adds no binding of its own,
// and Escape belongs to simplifySelection. Crossing to the render is a
// navigation key rather than an escape hatch, which is what Mod-Shift-Enter is
// for: it reads as "run, and go look".
const runKeymap = [
  { key: 'Mod-Enter',       preventDefault: true, run: () => { run(); return true; } },
  { key: 'Mod-Shift-Enter', preventDefault: true,
    run: () => { hidePreview(false); focusOnReady = true; run(); return true; } },
  { key: 'Mod-l',       preventDefault: true, run: () => { togglePreview(); return true; } },
  { key: 'F1', preventDefault: true, run: () => { shortcutsOpen(); return true; } },
  // Mod-Shift-s is left alone: Firefox takes it for screenshots.
  { key: 'Mod-s',       preventDefault: true, run: () => { saveZip(); return true; } },
  { key: 'Mod-e',       preventDefault: true, run: () => { saveSite(); return true; } },
  { key: 'Mod-o',       preventDefault: true, run: () => { fileInput.click(); return true; } },
  { key: 'Mod-Shift-o', preventDefault: true, run: () => { zipInput.click();  return true; } },
  { key: 'Mod-Shift-Backspace', run: () => { if (SEED) reset(); return !!SEED; } },
];

// ── The shortcuts panel ───────────────────────────────────────────────────────
// A key nobody is told about is trivia, not an affordance — and that applies to
// CodeMirror's built-in Escape-then-Tab as much as to anything bound here. So
// every key this pane answers is written down in one place, reachable by one
// key, and named on the bar.
//
// F1 rather than '?': the editor is a text field, and '?' typed into one is a
// character. rvmark's own '?' handler makes the same call from the other side —
// it ignores the key while something is taking typed text (isTyping in
// keymap.ts) — so the two panes each answer the key that is free in them.

const SHORTCUTS = [
  ['Ctrl+Enter',       'Run — render the buffer'],
  ['Ctrl+Shift+Enter', 'Run, then move to the render'],
  ['Ctrl+L',           'Show or hide the preview'],
  ['Ctrl+S',           'Save every file as a zip'],
  ['Ctrl+E',           'Export the built site as a zip'],
  ['Ctrl+O',           'Open files into the tree'],
  ['Ctrl+Shift+O',     'Open a zip, replacing the tree'],
  ...(SEED ? [['Ctrl+Shift+Backspace', 'Reset']] : []),
  ['F2',               'In the file tree, rename'],
  ['Delete',           'In the file tree, delete'],
  ['Escape',           'From the render, back to the editor'],
  ['Tab / Shift+Tab',  'Indent / dedent'],
  ['Escape then Tab',  'Move focus out of the editor'],
  ['Ctrl+M',           'Toggle Tab between indenting and moving focus'],
  ['Ctrl+B',           'Bold'],
  ['Ctrl+I',           'Italic'],
  ['Ctrl+F',           'Find in the buffer'],
  ['F1',               'This list'],
];

let shortcutsDialog = null;

function shortcutsOpen() {
  if (!shortcutsDialog) {
    shortcutsDialog = document.createElement('dialog');
    shortcutsDialog.id = 'shortcuts';
    shortcutsDialog.innerHTML =
      '<h2>Keyboard</h2><dl>' +
      SHORTCUTS.map(([k, what]) =>
        `<dt><kbd>${k.replace(/\+/g, '</kbd>+<kbd>').replace(/ \/ /g, '</kbd> / <kbd>')
                    .replace(/ then /g, '</kbd> then <kbd>')}</kbd></dt><dd>${what}</dd>`).join('') +
      '</dl><form method="dialog"><button>Close</button></form>';
    // Escape is native; a backdrop click is not. A click on the backdrop
    // targets the dialog element itself, so that comparison is the whole test.
    shortcutsDialog.addEventListener('click', (e) => {
      if (e.target === shortcutsDialog) shortcutsDialog.close();
    });
    document.body.appendChild(shortcutsDialog);
  }
  if (!shortcutsDialog.open) shortcutsDialog.showModal();
}

// ── The editor ────────────────────────────────────────────────────────────────
// basicSetup's contents, spelled out rather than imported: `indentWithTab` has
// to be added (it is deliberately absent from basicSetup, since capturing Tab
// costs keyboard users their way out of the field — here the language is
// indentation-structured and Tab has to indent), and Mod-Enter has to precede
// the default keymap. Listing the extensions is what makes both visible.

const extensions = [
  lineNumbers(),
  highlightActiveLineGutter(),
  // Tabs as fixed-width widgets: a native tab stop is measured from the
  // line's padded edge, which rvmarkWrapIndent moves.
  highlightSpecialChars({ addSpecialChars: /\t/ }),
  history(),
  lockCompartment.of(lock(true)),
  foldGutter(),
  drawSelection(),
  EditorState.allowMultipleSelections.of(true),
  indentOnInput(),
  themeCompartment.of(editorTheme(darkQuery.matches)),
  bracketMatching(),
  closeBrackets(),
  rectangularSelection(),
  crosshairCursor(),
  highlightActiveLine(),
  highlightSelectionMatches(),
  rvmarkLanguage,
  rvmarkFolding,
  EditorView.lineWrapping,
  rvmarkWrapIndent,
  indentationMarkers(),
  starWrapsSelection,
  richPaste(savePasted),
  // Highest precedence: these must beat defaultKeymap, which binds
  // Shift-Enter to inserting a newline and would otherwise swallow the
  // hand-over key and type into the document instead of running.
  Prec.highest(keymap.of(runKeymap)),
  keymap.of([
    { key: 'Enter', run: continueOutlineCommand },
    ...closeBracketsKeymap,
    ...emphasisKeymap,
    ...defaultKeymap,
    ...searchKeymap,
    ...historyKeymap,
    ...foldKeymap,
    indentWithTab,
  ]),
  // The draft, and the mark that the preview is behind it. Kept off the run
  // path so the preview and the draft never disagree about what was last typed.
  EditorView.updateListener.of((u) => {
    if (!u.docChanged || current === null) return;
    fs.write(current, u.state.doc.toString());
    status.classList.add('dirty');
  }),
];

const makeState = (doc) => EditorState.create({ doc, extensions });

const view = new EditorView({ parent: document.getElementById('editor'), state: makeState('') });

// ── Boot ──────────────────────────────────────────────────────────────────────
// The first frame load is a run like any other, so the frame never renders the
// on-disk file while the editor shows something else.

frame.addEventListener('load', () => {
  frame.classList.toggle('blank', frame.contentDocument?.URL === 'about:blank');
});

// What the preview says, through preview.html and relay.js. A key pressed in
// there never reaches this document, so relay.js sends up the ones answered
// here: hostKey's, and the way back. Mod-Shift-Enter is deliberately the same
// key both ways: one gesture that means "the other pane", whichever pane you
// are in. Escape returns too, since a reader who tabbed in wants out.
addEventListener('message', (e) => {
  if (e.source !== frame.contentWindow || e.origin !== PREVIEW) return;
  const { type } = e.data ?? {};
  if (type === 'rvmark-playground:boot') {
    if (handoff) frame.contentWindow.postMessage(handoff, PREVIEW);
  } else if (type === 'rvmark-playground:file') {
    answerFile(String(e.data.path), e.ports[0]);
  } else if (type === 'rvmark-playground:key') {
    const key = { ...e.data, preventDefault() {} };
    if (hostKey(key)) return;
    if (key.key === 'Escape' || (key.key === 'Enter' && key.shiftKey && (key.ctrlKey || key.metaKey))) view.focus();
  } else if (type === 'rvmark-playground:ready' && focusOnReady) {
    focusOnReady = false;
    focusPreview();
  }
});

/**
 * The keys that answer wherever focus is. F1 lists the playground's keys;
 * rvmark's own '?' still answers in the preview for the tree's keys, and the
 * two do not collide. Ctrl+S has to be caught everywhere, or the browser's own
 * save-page dialog takes it and the key looks broken on the side the reader
 * happens to be standing on.
 */
function hostKey(e) {
  const mod = e.ctrlKey || e.metaKey;
  if (e.key === 'F1') shortcutsOpen();
  else if (mod && !e.shiftKey && e.key.toLowerCase() === 's') saveZip();
  else if (mod && !e.shiftKey && e.key.toLowerCase() === 'e') saveSite();
  else if (mod && e.key.toLowerCase() === 'o') (e.shiftKey ? zipInput : fileInput).click();
  else if (mod && !e.shiftKey && e.key.toLowerCase() === 'l') togglePreview();
  else if (SEED && mod && e.shiftKey && e.key === 'Backspace') reset();
  else return false;
  e.preventDefault();
  return true;
}

// The editor answers these itself; this is for the rest
// of the host page, the file tree above all.
document.addEventListener('keydown', (e) => { if (!e.defaultPrevented) hostKey(e); });

fs.whenFull(() => tell(
  'Browser storage is full. Changes from here on are lost on reload; save a zip to keep them.'));

darkQuery.addEventListener('change', (e) => {
  view.dispatch({ effects: themeCompartment.reconfigure(editorTheme(e.matches)) });
});

// A filesystem that has been used wins over the file on disk: reopening the
// playground shows what you were writing, not what the page was built from. An
// empty one is seeded from welcome.rv.md, so a first visit has a buffer.
/** The preview's worker once ran on this origin too; it belongs to the preview's now. */
async function retireWorker() {
  try {
    for (const reg of await navigator.serviceWorker.getRegistrations()) await reg.unregister();
  } catch (_) {}
}

const STORE_LOCK = 'rvmark-playground:store';

/** A seeded workspace's lock; held by whichever tab has it open. */
const seedLock = (id) => `${STORE_LOCK}:${id}`;

/** A seeded workspace's folder name: its zip URL, hashed. */
async function seedId(url) {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(url));
  return Array.from(new Uint8Array(hash).slice(0, 16), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Wait to be the only tab on the store, and stay so until this one closes. Each
 * tab reads the disk once and writes from its own copy, so two would write over
 * one another.
 */
async function alone(name, busy) {
  if (!navigator.locks) return;
  const hold = (options) => new Promise((got) => {
    navigator.locks.request(name, options, (lock) => { got(!!lock); return lock && new Promise(() => {}); });
  });
  if (await hold({ ifAvailable: true })) return;

  const wait = document.createElement('dialog');
  if (busy) wait.textContent = busy;
  else wait.innerHTML = '<h2>Open in another tab</h2>The editor starts here once that tab is closed.';
  // Not to be dismissed: Escape is refused, and reopened where the browser closes it regardless.
  wait.addEventListener('cancel', (e) => e.preventDefault());
  wait.addEventListener('close', () => wait.showModal());
  document.body.appendChild(wait);
  wait.showModal();
  await hold({});
  wait.remove();
}

/** Remove the seeded workspaces nobody has opened for a day, and nobody has open. */
async function sweep(own) {
  if (!navigator.locks) return;
  try {
    const seeded = await (await navigator.storage.getDirectory()).getDirectoryHandle('seeded', { create: true });
    for await (const [id, dir] of seeded.entries()) {
      if (id === own || dir.kind !== 'directory') continue;
      let used = 0;
      try { used = JSON.parse(await (await (await dir.getFileHandle('meta')).getFile()).text()).used ?? 0; } catch (_) {}
      if (Date.now() - used < DAY) continue;
      await navigator.locks.request(seedLock(id), { ifAvailable: true }, async (lock) => {
        if (lock) await seeded.removeEntry(id, { recursive: true });
      });
    }
  } catch (err) {
    console.warn('[playground] sweep failed:', err);
  }
}

async function boot() {
  // The same build serves the preview origin, where the editor must not keep anything.
  if (location.origin !== ORIGINS.editor) {
    location.replace(ORIGINS.editor + location.pathname + location.search);
    return;
  }
  const id = SEED && await seedId(SEED);
  await (SEED ? alone(seedLock(id), BUSY) : alone(STORE_LOCK));
  if (!(await fs.load(SEED ? `seeded/${id}/` : ''))) {
    tell('This browser gives the editor no storage. Nothing here survives a reload.');
  }
  sweep(id);

  if (SEED) {
    await seed();
  } else if (fs.isEmpty()) {
    // ?welcome=nl asks for welcome.nl.rv.md; a name there is no file for gets the plain one.
    const named = /^[a-z-]+$/.exec(new URLSearchParams(location.search).get('welcome') ?? '');
    const get = (file) => fetch(file, { cache: 'no-cache' }).then((res) => res.ok ? res.text() : null, () => null);
    const text = (named && await get(`./welcome.${named[0]}.rv.md`)) ?? await get('./welcome.rv.md');
    fs.write(DEFAULT_FILE, text ?? '');
    setUnsaved(false);
  }

  openFirst(prefs.get(CURRENT_KEY));
  await retireWorker();
  run();
  view.focus();
}

boot();
